import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import { useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FETCH_ARTIST as fetchArtist,
  FETCH_ARTISTS_LIST as fetchArtistsList,
} from "../../../graphql/query/Artist";
import ArtistSearch from "./ArtistSearch";
import { artistFilterStore, artistNavigationStore } from "./useArtistFilters";
import NewArtist from "./Actions/NewArtist";
import routes from "../pages/Wizard/routes";
import AddContactAttribute from "./Actions/AddContactAttribute";
import useUser from "../../../hooks/useUser";
import { useMemo, useState } from "react";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import { useSearchSynopsis } from "../../../hooks/useSearchSynopsis";
import { clearSelectionState } from "../../../util/clearSelectionState";
import {
  getSearchAwareLabel,
  markModuleSearchSubmitted,
  useModuleSearchSubmitted,
} from "../../../util/usePersistedFilterFormik";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";
import {
  buildArtistListUrl,
  buildArtistNavigationTargetUrl,
  getArtistListContext,
} from "./searchResultNavigation";

const ARTISTS_NAVIGATION_QUERY = fetchArtistsList({});

const PrimaryNav = props => {
  const { formik } = props;
  const search = artistFilterStore.get();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const isSearchSubmitted = useModuleSearchSubmitted(location.pathname);
  const navigate = useNavigate();
  const pathnames = location?.pathname.split("/");
  const id = pathnames?.length > 2 ? pathnames[2] : null;
  const artistId = id ? Number(id) : null;
  const { hasPermission } = useUser();

  const artistObj = {
    contact: {},
  };
  const { data: { artist } = {} } = useQuery(fetchArtist(artistObj), {
    skip: !id,
    variables: { id: +id },
  });
  const contactId = artist?.contact?.id;
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listContext = useMemo(
    () => getArtistListContext(location.search),
    [location.search],
  );
  const listId = +(
    queryParams.get("listId") ||
    (status === "selectListWorking" ? queryParams.get("workingListId") : null)
  );
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "details",
    buildTargetUrl: buildArtistNavigationTargetUrl,
    dataKey: "artistsV2",
    fetchQuery: ARTISTS_NAVIGATION_QUERY,
    listContext,
    navigate,
    navigationStore: artistNavigationStore,
    recordId: artistId,
    status,
  });

  const statusMap = {
    all: "All Artists",
    searched: "Searched Artists",
    selectListWorking: "Select List (Working)",
  };
  const isShowingSearchResults = isSearchSubmitted && status === "all";
  const { synopsis: searchSynopsis } = useSearchSynopsis(artistNavigationStore);
  const label = getSearchAwareLabel(
    statusMap[status] || "All Artists",
    isShowingSearchResults,
  );
  const rootLabel =
    isShowingSearchResults && searchSynopsis
      ? { label, title: `Artist Search ${searchSynopsis}` }
      : label;
  const steps = [
    {
      label: rootLabel,
      onClick: () => navigate(buildArtistListUrl({ listContext, status })),
    },
    ...(artist
      ? [
          {
            label: artist?.name,
            onClick: () =>
              navigate(
                buildArtistNavigationTargetUrl({
                  listContext,
                  recordId: artist?.id,
                  status,
                }),
              ),
          },
          {
            label: route?.name,
            onClick: () =>
              navigate(
                buildArtistNavigationTargetUrl({
                  activeSubPage: route?.href,
                  listContext,
                  recordId: artist?.id,
                  status,
                }),
              ),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  const searchModalProps = {
    body: ArtistSearch,
    closeOnBackdrop: true,
    scale: "md",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => {
      clearSelectionState(formik);
      markModuleSearchSubmitted(location.pathname);
    },
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // here. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: id,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => clearSelectionState(formik),
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Artist",
        disabled: !hasPermission("CREATE_ARTIST"),
        modalProps: {
          body: NewArtist,
          disabled: !hasPermission("CREATE_ARTIST"),
          ...defaultActionProps,
        },
      },
      {
        disabled: !id,
        label: "Add Contact Attribute",
        modalProps: {
          body: AddContactAttribute,
          ...defaultActionProps,
          hideCloseButton: false,
          closeOnBackdrop: true,
          artist,
        },
      },
    ],
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <div id="form-save-status" />
        <Button
          label="Go to Contact"
          action="default"
          disabled={!contactId}
          onClick={() => navigate(`/contacts/${contactId}/details`)}
        />
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...searchModalProps}>
          <Button
            label="Artist Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} commandPalette />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
